/**
 * Hook for server-side Products grid data
 *
 * Fetches data from the in-memory search index with pagination, sorting, and filtering.
 * Designed to work with MUI Data Grid Premium server-side mode.
 *
 * Products are READ-ONLY - editing happens within deals.
 *
 * Uses React Query for cache management and invalidation.
 * Invalidate with: queryClient.invalidateQueries({ queryKey: ['search', 'products'] })
 */

import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://grobotic.app:2096'

/** Query key for products search - use for invalidation */
export const PRODUCTS_SEARCH_KEY = ['search', 'products'] as const

/** Change-type filter values accepted by the backend toolbar filter. */
export type ChangeTypeFilter = 'New Business' | 'Upsell' | 'Downsell' | 'Renewal' | 'Removed'

interface SearchParams {
  page: number
  pageSize: number
  sortModel?: GridSortModel
  filterModel?: GridFilterModel
  /** Toolbar-level month filter — 'YYYY-MM'. Limits to products active in that month. */
  month?: string
  /** Toolbar-level change-type filter. */
  changeType?: ChangeTypeFilter
}

interface SearchResult {
  rows: any[]
  totalCount: number
  page: number
  pageSize: number
  aggregations?: {
    price: number
    netTotal: number
    total: number
    mediaSpend: number
    grossProfit: number
    /** Sum of the Change-column dollar deltas across the full filtered set. */
    change_type: number
  }
}

interface UseServerSideProductsOptions {
  enabled?: boolean
  pageSize?: number
  initialSortModel?: GridSortModel
  initialFilterModel?: GridFilterModel
}

interface UseServerSideProductsReturn {
  // Data
  rows: any[]
  totalRowCount: number
  aggregations?: SearchResult['aggregations']

  // Pagination
  page: number
  pageSize: number
  onPageChange: (newPage: number) => void
  onPageSizeChange: (newPageSize: number) => void

  // Sorting
  sortModel: GridSortModel
  onSortModelChange: (newSortModel: GridSortModel) => void

  // Filtering
  filterModel: GridFilterModel
  onFilterModelChange: (newFilterModel: GridFilterModel) => void

  // Toolbar filters
  month: string | undefined
  setMonth: (m: string | undefined) => void
  changeType: ChangeTypeFilter | undefined
  setChangeType: (t: ChangeTypeFilter | undefined) => void

  // Loading state
  isLoading: boolean
  error: string | null

  // Refresh
  refresh: () => Promise<void>

  // Server-side mode props for DataGridPremium
  serverSideProps: {
    paginationMode: 'server'
    sortingMode: 'server'
    filterMode: 'server'
    rowCount: number
    paginationModel: { page: number; pageSize: number }
    onPaginationModelChange: (model: { page: number; pageSize: number }) => void
    loading: boolean
  }
}

/**
 * Fetch function for the products search API
 */
const fetchProductsSearch = async (params: SearchParams): Promise<SearchResult> => {
  const response = await fetch(`${API_BASE}/search/products`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Hook for fetching products with server-side pagination, sorting, and filtering
 */
export const useServerSideProducts = (options: UseServerSideProductsOptions = {}): UseServerSideProductsReturn => {
  const {
    enabled = true,
    pageSize: initialPageSize = 50,
    initialSortModel = [{ field: 'productName', sort: 'asc' }],
    initialFilterModel = { items: [], quickFilterValues: [] },
  } = options

  const queryClient = useQueryClient()

  // Controlled state for grid parameters
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel)
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel)
  const [month, setMonthState] = useState<string | undefined>(undefined)
  const [changeType, setChangeTypeState] = useState<ChangeTypeFilter | undefined>(undefined)

  // Debounced filter model for the query
  const [debouncedFilterModel, setDebouncedFilterModel] = useState<GridFilterModel>(initialFilterModel)
  const filterDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Debounce filter changes
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current)
    }

    filterDebounceRef.current = setTimeout(() => {
      setDebouncedFilterModel(filterModel)
      setPage(0)
    }, 300)

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current)
      }
    }
  }, [filterModel])

  // Build query key with all search parameters
  const queryKey = [
    ...PRODUCTS_SEARCH_KEY,
    { page, pageSize, sortModel, filterModel: debouncedFilterModel, month, changeType },
  ] as const

  // Use React Query for data fetching
  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: () =>
      fetchProductsSearch({
        page,
        pageSize,
        sortModel,
        filterModel: debouncedFilterModel,
        month,
        changeType,
      }),
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    placeholderData: previousData => previousData,
  })

  // Extract data from query result
  const rows = data?.rows ?? []
  const totalRowCount = data?.totalCount ?? 0
  const aggregations = data?.aggregations

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const onPageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }, [])

  const onSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel)
    setPage(0)
  }, [])

  const onFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel)
  }, [])

  // Toolbar filter setters reset to page 0 so the user doesn't land on an
  // empty page after narrowing the row set.
  const setMonth = useCallback((m: string | undefined) => {
    setMonthState(m)
    setPage(0)
  }, [])

  const setChangeType = useCallback((t: ChangeTypeFilter | undefined) => {
    setChangeTypeState(t)
    setPage(0)
  }, [])

  const onPaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    setPage(model.page)
    setPageSize(model.pageSize)
  }, [])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: PRODUCTS_SEARCH_KEY })
  }, [queryClient])

  // Props to spread onto DataGridPremium for server-side mode
  const serverSideProps = {
    paginationMode: 'server' as const,
    sortingMode: 'server' as const,
    filterMode: 'server' as const,
    rowCount: totalRowCount,
    paginationModel: { page, pageSize },
    onPaginationModelChange,
    loading: isLoading || isFetching,
  }

  return {
    rows,
    totalRowCount,
    aggregations,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    month,
    setMonth,
    changeType,
    setChangeType,
    isLoading: isLoading || isFetching,
    error: queryError?.message ?? null,
    refresh,
    serverSideProps,
  }
}

/**
 * Check if server-side products search is available
 */
export const checkProductsSearchHealth = async (): Promise<{
  available: boolean
  message: string
  documentCount?: number
  lastSyncTime?: string
  isInitialized?: boolean
}> => {
  try {
    const response = await fetch(`${API_BASE}/search/health`)
    const data = await response.json()

    return {
      available: data.healthy && data.products?.ready,
      message: data.products?.ready ? 'Products search available' : 'Products search unavailable',
      documentCount: data.products?.documentCount,
      lastSyncTime: data.products?.lastSyncTime,
      isInitialized: data.products?.isInitialized,
    }
  } catch (err) {
    return {
      available: false,
      message: 'Search service not reachable',
    }
  }
}

/**
 * Trigger a full products sync (admin only)
 */
export const triggerProductsFullSync = async (): Promise<{ success: boolean; indexed?: number; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/search/products/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.message || 'Sync failed' }
    }

    return { success: true, indexed: data.indexed }
  } catch (err: any) {
    return { success: false, error: err.message }
  }
}

/**
 * Get aggregations grouped by a field (for pivot table / analysis)
 */
export const fetchProductsAggregations = async (
  field: string
): Promise<{
  field: string
  aggregations: Array<{
    [key: string]: string | number
    count: number
    price: number
    netTotal: number
    total: number
    mediaSpend: number
    grossProfit: number
  }>
  totalGroups: number
}> => {
  const response = await fetch(`${API_BASE}/search/products/aggregations/${field}`)

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregations: ${response.statusText}`)
  }

  return response.json()
}

export default useServerSideProducts
